import AsidePageMenu from '@cohort/merchants/components/AsidePageMenu';
import type FeatureFlags from '@cohort/merchants/featureFlags';
import {useChallengeSteps} from '@cohort/merchants/hooks/api/Campaigns';
import {useCurrentCampaign} from '@cohort/merchants/hooks/contexts/currentCampaign';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {useCampaignPageStore} from '@cohort/merchants/hooks/stores/campaignPage';
import {MenuSkeleton} from '@cohort/merchants/layouts/SkeletonLoader';
import {
  getCampaignActivityRoute,
  getCampaignAudienceRoute,
  getCampaignNotificationRoute,
  getCampaignOverviewRoute,
  getCampaignRewardRoute,
  getCampaignSettingsRoute,
  getCampaignStepCompletionsRoute,
} from '@cohort/merchants/lib/Pages';
import {
  Bell,
  ChartLine,
  Gear,
  Gift,
  ListChecks,
  ListMagnifyingGlass,
  Users,
} from '@phosphor-icons/react';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {match} from 'ts-pattern';

const CampaignPageMenu = (): JSX.Element | null => {
  const merchant = useCurrentMerchant();
  const campaign = useCurrentCampaign();
  const {t} = useTranslation('pages', {keyPrefix: 'campaigns.campaign.campaignPageMenu'});
  const menu = useCampaignPageStore(state => state.menu);
  const {featureCampaignsAnalytics, featureStepCompletionsPage, featureChallengeContents} =
    useFlags<FeatureFlags>();

  const {data: steps} = useChallengeSteps(merchant.id, campaign.id, {
    enabled: campaign.type === 'challenge',
  });

  if (campaign.type === 'challenge' && steps === undefined) {
    return <MenuSkeleton />;
  }

  if (menu) {
    return <Fragment>{menu}</Fragment>;
  }

  const settingsAnchors = match(campaign.type)
    .with('challenge', () => [
      {
        name: t('appearance'),
        value: 'appearance',
      },
      {
        name: t('access'),
        value: 'access',
      },
      {
        name: t('steps'),
        value: 'steps',
      },
      ...(featureChallengeContents
        ? [
            {
              name: t('contents'),
              value: 'contents',
            },
          ]
        : []),
      {
        name: t('options'),
        value: 'options',
      },
      {
        name: t('customerInformation'),
        value: 'customer-information',
      },
    ])
    .with('store', () => [
      {
        name: t('appearance'),
        value: 'appearance',
      },
      {
        name: t('access'),
        value: 'access',
      },
      {
        name: t('price'),
        value: 'price',
      },
      {
        name: t('customerInformation'),
        value: 'customer-information',
      },
    ])
    .otherwise(() => []);

  return (
    <AsidePageMenu
      entries={[
        ...(campaign.status !== 'draft'
          ? [
              ...(campaign.type === 'challenge' && featureCampaignsAnalytics
                ? [
                    {
                      name: t('overview'),
                      path: getCampaignOverviewRoute(campaign.id).path,
                      icon: <ChartLine size={20} />,
                    },
                  ]
                : []),
              {
                name:
                  campaign.type === 'airdrop'
                    ? t('recipients')
                    : campaign.type === 'store'
                      ? t('orders')
                      : t('participants'),
                path: getCampaignActivityRoute(campaign.id).path,
                icon: <ListMagnifyingGlass size={20} />,
              },
              ...((steps !== undefined && steps.length > 0 && featureStepCompletionsPage) ||
              campaign.referralConfiguration !== null
                ? [
                    {
                      name: t('stepCompletions'),
                      path: getCampaignStepCompletionsRoute(campaign.id).path,
                      icon: <ListChecks size={20} />,
                    },
                  ]
                : []),
              {
                name: t('audience'),
                path: getCampaignAudienceRoute(campaign.id).path,
                icon: <Users size={20} />,
              },
              {
                name: t('reward'),
                path: getCampaignRewardRoute(campaign.id).path,
                icon: <Gift size={20} />,
              },
              // @Devs - We do not want to show the notifications and settings links for airdrop campaigns.
              ...(campaign.type !== 'airdrop'
                ? [
                    {
                      name: t('notifications'),
                      path: getCampaignNotificationRoute(campaign.id).path,
                      icon: <Bell size={20} />,
                    },
                    {
                      name: t('settings'),
                      path: getCampaignSettingsRoute(campaign.id).path,
                      icon: <Gear size={20} />,

                      anchors: settingsAnchors,
                    },
                  ]
                : []),
            ]
          : []),
      ]}
    />
  );
};

export default CampaignPageMenu;
